import { Box } from "@mui/material";
import { useAnalyticsData } from "modules/customerTickets/hooks/useAnalyticsData";
import React, { FC } from "react";
import {
  COLOR_DARK_GREEN,
  COLOR_DARK_RED,
  COLOR_GREY_10,
} from "shared/theme/colors";
import { ArrowCurvedDownIcon } from "shared/theme/icons/ArrowCurvedDownIcon";
import { ArrowCurvedUpIcon } from "shared/theme/icons/ArrowCurvedUpIcon";
import { ArrowLongForwardIcon } from "shared/theme/icons/ArrowLongForwardIcon";
import { StyledBlackText, StyledComparisonIndicator } from "./styled";

const ComparingSection: FC = () => {
  const {
    lastWeekAverageResponseTime,
    thisWeekAverageResponseTime,
    weeklyResponseTimeDifferencePercentage,
  } = useAnalyticsData();

  const weeklyResponseTimeDifference =
    thisWeekAverageResponseTime - lastWeekAverageResponseTime;
  const isResponseTimeEqual =
    thisWeekAverageResponseTime === lastWeekAverageResponseTime;
  const isIncreased = weeklyResponseTimeDifference > 0;

  const formatTimeDifference = (diffInSeconds: number) => {
    const absSeconds = Math.abs(diffInSeconds);
    const hours = Math.floor(absSeconds / 3600);
    const minutes = Math.floor((absSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "hour" : "hours"}`;
    }

    if (minutes > 0) {
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }

    return `${absSeconds} ${absSeconds === 1 ? "second" : "seconds"}`;
  };

  const isLowerRatio =
    (weeklyResponseTimeDifferencePercentage > 0 &&
      weeklyResponseTimeDifferencePercentage < 1) ||
    weeklyResponseTimeDifferencePercentage === 0;

  const renderIcon = () => {
    if (isResponseTimeEqual || weeklyResponseTimeDifferencePercentage === 0) {
      return <ArrowLongForwardIcon color={COLOR_GREY_10} />;
    } else if (isIncreased) {
      return (
        <ArrowCurvedUpIcon
          color={isLowerRatio ? COLOR_GREY_10 : COLOR_DARK_RED}
        />
      );
    } else if (!isIncreased) {
      return (
        <ArrowCurvedDownIcon
          color={isLowerRatio ? COLOR_GREY_10 : COLOR_DARK_GREEN}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        columnGap: "8px",
      }}
    >
      {lastWeekAverageResponseTime > 0 && (
        <StyledComparisonIndicator
          isIncreased={isIncreased}
          isLowerRatio={isLowerRatio}
        >
          {renderIcon()}
          {weeklyResponseTimeDifferencePercentage === 0
            ? "0.0"
            : weeklyResponseTimeDifferencePercentage}
          %
        </StyledComparisonIndicator>
      )}
      {!isResponseTimeEqual && weeklyResponseTimeDifferencePercentage !== 0 && (
        <StyledBlackText>
          {`(${isIncreased ? "+" : "-"}${formatTimeDifference(weeklyResponseTimeDifference)} this week)`}
        </StyledBlackText>
      )}
    </Box>
  );
};

export default ComparingSection;
